/**
 * TanStack Query hooks for sessions: the timeline, one day's detail and the program phase timeline.
 */

import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type {
  TimelineResponse,
  DayDetailOut,
  PlanTimelineEntry,
} from '../types'

// ── Sessions (paginated, filterable) ────────────────────────────────

export interface UseSessionsParams {
  limit?: number
  offset?: number
  date_from?: string
  date_to?: string
  plan?: string
}

/**
 * Fetch the unified training timeline — one entry per day (Milon + cardio).
 */
export function useTimeline(limit = 5000) {
  return useQuery({
    queryKey: ['timeline', limit],
    queryFn: () => apiFetch<TimelineResponse>(`/api/timeline?limit=${limit}`),
  })
}

/**
 * Fetch the combined detail for a single training day (Milon + all cardio).
 * Disabled until a date is selected.
 */
export function useDayDetail(date: string | null) {
  return useQuery({
    queryKey: ['day-detail', date],
    queryFn: () => apiFetch<DayDetailOut>(`/api/days/${date}/detail`),
    enabled: date !== null,
  })
}

// ── All Sessions (for attendance / heatmap) ─────────────────────────

/**
 * Every training day tagged with its program, across all sources (Milon +
 * free-weight Lanista workouts). Powers the program phase timeline.
 * Query key: ['sessions', 'plan-timeline']
 */
export function usePlanTimeline() {
  return useQuery({
    queryKey: ['sessions', 'plan-timeline'],
    queryFn: () => apiFetch<PlanTimelineEntry[]>('/api/sessions/plan-timeline'),
  })
}
